import React from 'react';
import { Card, Row, Col, Badge, ProgressBar, Button } from 'react-bootstrap';
import {
  User, Phone, Calendar, Clock, DollarSign, AlertTriangle,
  ChevronRight, Shirt
} from 'lucide-react';
import customOrderService from '../../../services/customOrderService';
import modelService from '../../../services/modelService'; // ✅ NOUVEAU
import OrderStatusBadge from './OrderStatusBadge';

/**
 * ✅ ATELIER: Card d'affichage résumée d'une commande.
 */
const OrderCard = ({ order, onClick }) => {
  const isOverdue = order.isOverdue && !['DELIVERED', 'CANCELLED'].includes(order.status);
  const isUrgent = order.priority === 'URGENT';

  // Couleur de bordure selon urgence / retard
  let borderColor = 'transparent';
  if (isOverdue) borderColor = '#dc3545';
  else if (isUrgent) borderColor = '#fd7e14';

  // ✅ NOUVEAU: URL absolue de la vignette du modèle
  const thumbnailUrl = modelService.buildImageUrl(order.modelImageUrl);

  return (
    <Card
      className="order-card h-100 shadow-sm"
      onClick={() => onClick?.(order)}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        borderLeft: `4px solid ${borderColor}`
      }}
    >
      <Card.Body>
        {/* En-tête : numéro + statut */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div className="fw-bold text-primary" style={{ fontSize: '0.95rem' }}>
              {order.orderNumber}
            </div>
            <small className="text-muted">
              Créée le {customOrderService.formatDate(order.createdAt)}
            </small>
          </div>
          <OrderStatusBadge status={order.status} />
        </div>

        {/* Client */}
        <div className="mb-2">
          <div className="d-flex align-items-center gap-1">
            <User size={14} className="text-secondary" />
            <strong>{order.customerName}</strong>
          </div>
          {order.customerPhone && (
            <small className="text-muted d-flex align-items-center gap-1">
              <Phone size={10} />
              {order.customerPhone}
            </small>
          )}
        </div>

        {/* ✅ NOUVEAU: Modèle avec vignette */}
        <div className="mb-2 d-flex align-items-center gap-2">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={order.modelName || 'Modèle'}
              className="order-model-thumb"
              onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div
            className="order-model-thumb-placeholder"
            style={{ display: thumbnailUrl ? 'none' : 'flex' }}
          >
            <Shirt size={16} />
          </div>
          <span className="text-truncate text-muted small">
            {order.modelName || 'Modèle personnalisé'}
          </span>
        </div>

        {/* Badges urgence / retard */}
        {(isUrgent || isOverdue) && (
          <div className="mb-2 d-flex gap-1 flex-wrap">
            {isUrgent && (
              <Badge bg="warning" text="dark">
                🔴 Urgent
              </Badge>
            )}
            {isOverdue && (
              <Badge bg="danger" className="d-flex align-items-center gap-1">
                <AlertTriangle size={10} />
                En retard
              </Badge>
            )}
          </div>
        )}

        {/* Livraison */}
        <Row className="g-2 mb-3 text-muted small">
          <Col xs={12}>
            <div className="d-flex align-items-center gap-1">
              <Calendar size={13} />
              Livraison :
              <strong className="ms-1 text-dark">
                {customOrderService.formatDate(order.expectedDeliveryDate)}
              </strong>
            </div>
            <div className={`d-flex align-items-center gap-1 ${
              isOverdue ? 'text-danger fw-semibold' : 'text-muted'
            }`}>
              <Clock size={13} />
              {customOrderService.formatDaysUntil(order.daysUntilDelivery)}
            </div>
          </Col>
        </Row>

        {/* Paiement */}
<div className="payment-info border-top pt-2">
  <div className="d-flex justify-content-between align-items-center mb-1">
    <span className="small text-muted d-flex align-items-center gap-1">
      <DollarSign size={13} />
      Paiement
    </span>
    <small className="fw-semibold">
      {order.paymentPercentage}%
    </small>
  </div>

  {/* ✅ Affichage remise (si applicable) */}
  {order.discountAmount > 0 && (
    <div className="discount-info bg-success bg-opacity-10 rounded px-2 py-1 mb-2">
      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          <span style={{ fontSize: '0.7rem' }}>🏷️</span> Sous-total :
        </small>
        <small className="text-decoration-line-through text-muted">
          {customOrderService.formatPrice(order.subtotal)}
        </small>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <small className="text-success fw-semibold">
          Remise {order.discountType === 'PERCENTAGE'
            ? `(${order.discountValue}%)`
            : 'fixe'} :
        </small>
        <small className="text-success fw-semibold">
          -{customOrderService.formatPrice(order.discountAmount)}
        </small>
      </div>
    </div>
  )}

  <ProgressBar
    now={order.paymentPercentage}
    variant={order.isFullyPaid ? 'success' : 'info'}
    style={{ height: '6px' }}
  />

  <div className="d-flex justify-content-between mt-1">
    <small className="text-success">
      {customOrderService.formatPrice(order.advanceAmount)}
    </small>
    <small className="text-muted">
      / {customOrderService.formatPrice(order.totalPrice)}
    </small>
  </div>

  {!order.isFullyPaid && order.status !== 'CANCELLED' && (
    <small className="text-warning fw-semibold d-block mt-1">
      Reste : {customOrderService.formatPrice(order.remainingAmount)}
    </small>
  )}
</div>

        {/* Indicateur action */}
        <div className="text-end mt-2">
          <small className="text-primary d-flex align-items-center justify-content-end gap-1">
            Voir détails <ChevronRight size={14} />
          </small>
        </div>
      </Card.Body>

      <style>{`
        .order-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        /* ✅ NOUVEAU: Vignette modèle */
        .order-model-thumb {
          width: 36px;
          height: 36px;
          border-radius: 6px;
          object-fit: cover;
          flex-shrink: 0;
          border: 1px solid #e9ecef;
        }

        .order-model-thumb-placeholder {
          width: 36px;
          height: 36px;
          border-radius: 6px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #adb5bd;
          flex-shrink: 0;
          border: 1px solid #e9ecef;
        }
      `}</style>
    </Card>
  );
};

export default OrderCard;